import {
  Column,
  ColumnKeys,
  EasmAssetsInventoryGridViewData,
  NextScanInclusionStatus,
  ScanAction,
  SMART_VIEW_MAX_INPUT_LENGTH,
} from '../types';
import { ColumnDetails, ColumnType, ColumnValue } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { CategoriesConfig } from '../../../grid/genericGridSmartViews/types';
import { EnumUtils } from '../../../utils/enum.utils';
import { SmartViewInlineActions } from '../../../grid/gridSmartViews/types';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { FindingType, ExpirationStatus } from '../../types';
import { EasmInventoryBaseViewDataProvider } from './EasmInventoryBaseViewDataProvider';
import { ListItem } from '../../../ui/gzSingleSelectList/types';

export class EasmAssetsInventoryGridViewDataProvider extends EasmInventoryBaseViewDataProvider {
  public async getDataForView(): Promise<EasmAssetsInventoryGridViewData> {
    const scanActionOptions = this.getScanActionOptions(),
      investigationStatusOptions = this.getInvestigationStatusOptions(),
      priorityOptions = this.getPriorityOptions(),
      predefinedFilterKeys = [
        ColumnKeys.NAME,
        ColumnKeys.TYPE,
        ColumnKeys.INVESTIGATION_STATUS,
        ColumnKeys.ASSIGNEE,
        ColumnKeys.PRIORITY,
        ColumnKeys.NEXT_SCAN_STATUS,
        ColumnKeys.ASSETS_RELATED_TO_ASSET,
        ColumnKeys.ARTIFACTS_RELATED_TO_ASSET,
      ];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      predefinedFilterKeys.unshift(ColumnKeys.COMPANY);
    }

    return {
      smartViewsCategories: this.getSmartViewsCategories(),
      allColumns: await this.getAllColumns(investigationStatusOptions, priorityOptions),
      predefinedFilterKeys: predefinedFilterKeys,
      scanActionOptions,
      investigationStatusOptions,
      assigneeOptions: await this.getAssigneeOptions(),
      priorityOptions,
    };
  }

  private getSmartViewsCategories(): CategoriesConfig {
    return {
      internalCategories: {
        recent: {
          isAvailable: false,
        },
        default: {
          isAvailable: true,
          translations: {
            all_assets: translateFn('All assets'),
            discovered_ips: translateFn('IP information'),
            discovered_emails: translateFn('Emails'),
            discovered_domains: translateFn('Domains'),
            assets_assigned_to_you: translateFn('Assets assigned to you'),
            newest_assets: translateFn('New assets'),
          },
        },
        favorites: {
          isAvailable: true,
        },
      },
      customCategories: [
        {
          sectionTitle: this.dependencies.translateFn('Saved'),
          emptyStateItemText: this.dependencies.translateFn('No queries added'),
          type: 'saved',
          inlineActions: [
            {
              title: this.dependencies.translateFn('Rename'),
              value: SmartViewInlineActions.RENAME,
            },
            {
              title: this.dependencies.translateFn('Delete'),
              value: SmartViewInlineActions.DELETE,
              customClass: 'danger',
            },
          ],
          isOpen: false,
          smartViewMaxInputLength: SMART_VIEW_MAX_INPUT_LENGTH,
          noSpaceEscape: true,
          smartViewValidators: [],
          items: [],
        },
      ],
    };
  }

  private async getAllColumns(
    investigationStatusOptions: ColumnValue[],
    priorityOptions: ColumnValue[]
  ): Promise<Array<ColumnDetails<Column, ColumnKeys>>> {
    const columns: any = [
      {
        column: Column.NAME,
        key: ColumnKeys.NAME,
        columnName: translateFn('Asset name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        remoteDetails: {
          type: RemoteDetailsTypes.EASMAssetsName,
        },
        removable: false,
        sortable: false,
        width: '240px',
      },
      {
        column: Column.TYPE,
        key: ColumnKeys.TYPE,
        columnName: translateFn('Asset type'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<FindingType>([
          FindingType.DOMAIN,
          FindingType.SIMILAR_DOMAIN,
          FindingType.IP_4,
          FindingType.IP_6,
          FindingType.EMAIL,
        ]).map(
          (type: FindingType): ColumnValue => ({
            label: this.stringsTranslator.translateEASMType(type),
            value: type,
          })
        ),
        removable: false,
        filterResetValue: [],
        sortable: false,
      },
      {
        column: Column.NEXT_SCAN_STATUS,
        key: ColumnKeys.NEXT_SCAN_STATUS,
        columnName: translateFn('Asset status'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<NextScanInclusionStatus>(NextScanInclusionStatus).map(
          (status: NextScanInclusionStatus): ColumnValue => ({
            label: this.stringsTranslator.translateNextScanInclusionStatus(status),
            value: status,
          })
        ),
        removable: false,
        filterResetValue: [],
        sortable: false,
      },
      {
        column: Column.INVESTIGATION_STATUS,
        key: ColumnKeys.INVESTIGATION_STATUS,
        columnName: translateFn('Investigation status'),
        type: ColumnType.ENUM,
        values: investigationStatusOptions,
        removable: false,
        filterResetValue: [],
        sortable: false,
      },
      {
        column: Column.ASSIGNEE,
        key: ColumnKeys.ASSIGNEE,
        columnName: translateFn('Assignee'),
        type: ColumnType.SELECT,
        values: await this.getAssigneeFilterOptions(),
        removable: false,
        sortable: false,
      },
      {
        column: Column.PRIORITY,
        key: ColumnKeys.PRIORITY,
        columnName: translateFn('Priority'),
        type: ColumnType.ENUM,
        values: priorityOptions,
        removable: false,
        filterResetValue: [],
        sortable: false,
      },
      {
        column: Column.ASSETS_RELATED_TO_ASSET,
        key: ColumnKeys.ASSETS_RELATED_TO_ASSET,
        columnName: translateFn('Related assets'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.EASMAssetsRelatedAssets,
        },
        editingDisabled: true,
      },
      {
        column: Column.ARTIFACTS_RELATED_TO_ASSET,
        key: ColumnKeys.ARTIFACTS_RELATED_TO_ASSET,
        columnName: translateFn('Artifacts'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.EASMAssetsRelatedArtifacts,
        },
        editingDisabled: true,
      },
      {
        column: Column.NOTES,
        key: ColumnKeys.NOTES,
        columnName: translateFn('Notes'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
      },
      {
        column: Column.SERVICES_CVES,
        key: ColumnKeys.SERVICES_CVES,
        columnName: translateFn('CVEs'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
      },
      {
        column: Column.SERVICES_PORT,
        key: ColumnKeys.SERVICES_OPEN_PORTS,
        columnName: translateFn('Open ports'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
      },
      {
        column: Column.CERTIFICATE_REPORTS_EXPIRATION_STATUS,
        key: ColumnKeys.CERTIFICATE_REPORTS_EXPIRATION_STATUS,
        columnName: translateFn('Certificate expiration status'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<ExpirationStatus>(ExpirationStatus).map(
          (status: ExpirationStatus): ColumnValue => ({
            label: this.stringsTranslator.translateExpirationStatusInAssets(status),
            value: status,
          })
        ),
        removable: true,
        sortable: false,
      },
      {
        column: Column.VULNERABLE_TO_ZONE_TRANSFER,
        key: ColumnKeys.VULNERABLE_TO_ZONE_TRANSFER,
        columnName: translateFn('Vulnerable to zone transfer'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
      },
      {
        column: Column.DNS_RESOLUTION_STATUS,
        key: ColumnKeys.DNS_RESOLUTION_FAILS,
        columnName: translateFn('DNS resolution failed'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
      },
      {
        column: Column.NEW_ASSETS,
        key: ColumnKeys.NEW_ASSETS,
        columnName: translateFn('Show only new assets'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Yes'),
            value: 1,
          },
          {
            label: translateFn('No'),
            value: 0,
          },
        ],
        removable: true,
        sortable: false,
        isHiddenInGridSettings: true,
      },
      {
        column: Column.CHANGE_PROPERTY_ACTIONS,
        key: ColumnKeys.CHANGE_PROPERTY_ACTIONS,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
      },
    ];
    if (this.dependencies.permissions.canHaveChildCompanies) {
      columns.unshift({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: false,
        sortable: false,
        filterResetValue: {
          exactMatch: (Application.Main as any).user.company.id,
        },
      });
    }
    return columns;
  }

  protected getScanActionOptions(): (ColumnValue | ListItem)[] {
    return EnumUtils.GetNumericValues<ScanAction>(ScanAction).map(
      (scanAction: ScanAction): ColumnValue => ({
        label: this.stringsTranslator.translateScanAction(scanAction),
        value: scanAction,
      })
    );
  }
}
